import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import { useAuth } from 'api';
import {
  Container,
  Interaction,
  PlayByPlay,
  PlayHome,
  PlayList,
  SignIn,
} from 'components';

import { useFontLoader } from 'hooks';
import { renderMessage } from 'utils';
import { OverlayData } from 'types';
import AppContext from 'context';

import styles from './App.module.scss';

const App = () => {
  const [overlay, _setOverlay] = useState<OverlayData | null>(null);

  const auth = useAuth();
  const loaded = useFontLoader();

  useEffect(() => {
    if (overlay?.type === 'signin' && overlay.delay) {
      const timer = setTimeout(() => _setOverlay({ type: 'signin' }), overlay.delay);
      return () => clearTimeout(timer);
    }
  }, [overlay]);

  const setOverlay = useCallback(
    (overlay: OverlayData | null) =>
      _setOverlay(state => {
        if (state?.type === 'interaction') {
          if (overlay) {
            return state;
          }

          if (!auth.validation) {
            return { type: 'signin' };
          }
        }

        if (overlay?.type === 'signin') {
          return auth.validation ? null : overlay;
        }

        return overlay;
      }),
    [auth.validation]
  );

  const onOverlayClose = useCallback(() => setOverlay(null), [setOverlay]);

  if (auth.status === 'creating' || !loaded) {
    return null;
  }

  if (auth.status === 'error') {
    return <div className={styles.error}>{renderMessage('play.auth.error.unknown')}</div>;
  }

  const renderOverlay = () => {
    if (overlay) {
      switch (overlay.type) {
        case 'interaction':
          return <Interaction {...overlay.payload} onClose={onOverlayClose} />;

        case 'signin':
          return auth.validation || overlay.delay ? null : (
            <SignIn onClose={onOverlayClose} />
          );

        case 'plays':
          return <PlayByPlay {...overlay.payload} onClose={onOverlayClose} />;

        default:
          return null;
      }
    }
  };

  return (
    <AppContext.Provider value={{ auth, overlay, setOverlay }}>
      <Container>
        <Routes>
          <Route path="/play/:pathname" element={<PlayHome />} />
          <Route path="*" element={<PlayList />} />
        </Routes>

        {renderOverlay()}
      </Container>
    </AppContext.Provider>
  );
};

export default App;
